import { useState, useEffect } from "react";
import {
  Select,
  Input,
  Button,
  Modal,
  Form,
  InputNumber,
  Popover,
  Switch,
  Tooltip,
} from "antd";
import {
  SettingOutlined,
  FilterOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { fetchAiProfiles, AiProfile } from "@/lib/api/resources/agents";
import axios from "axios";

interface InferenceOptionsOverrideEditorProps {
  value?: Record<string, any> | null;
  profileId?: string | null;
  onChangeProfile: (profileId: string | null) => void;
  onChangeOverride: (override: Record<string, any> | null) => void;
  label?: React.ReactNode;
  help?: React.ReactNode;
  profileFilter?: (profile: AiProfile) => boolean;
  // When true, only the AI Profile selector is rendered; the inference-options override UI is hidden.
  // Used where the override is captured through dedicated form fields instead (e.g. the agent form).
  hideOverrides?: boolean;
}

export const InferenceOptionsOverrideEditor = ({
  value,
  profileId,
  onChangeProfile,
  onChangeOverride,
  label = "AI Profile & Overrides",
  help,
  profileFilter,
  hideOverrides = false,
}: InferenceOptionsOverrideEditorProps) => {
  const [isAdvancedModalVisible, setIsAdvancedModalVisible] = useState(false);
  const [form] = Form.useForm();

  const [freeOnly, setFreeOnly] = useState(false);
  const [minContext, setMinContext] = useState<number>(0);
  const [modalityFilter, setModalityFilter] = useState<string>("all");

  const { data: modelsResponse } = useQuery({
    queryKey: ["openrouter_models"],
    queryFn: async () => {
      const res = await axios.get("https://openrouter.ai/api/v1/models");
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: profilesResponse, isLoading } = useQuery({
    queryKey: ["ai_profiles"],
    queryFn: fetchAiProfiles,
  });

  const profiles = Array.isArray(profilesResponse)
    ? profilesResponse
    : (profilesResponse as any)?.member ||
      (profilesResponse as any)?.["hydra:member"] ||
      [];

  // Initialize form when modal opens
  useEffect(() => {
    if (isAdvancedModalVisible) {
      const overrides = value || {};
      // Support both the nested `{ options: { temperature } }` shape (benchmark) and
      // the legacy flat `{ temperature }` shape (agent read-only echo).
      const options = overrides.options || {};
      const temperature = options.temperature ?? overrides.temperature;

      // Build the "additional options" JSON from the options map, excluding the
      // temperature field which has its own dedicated input.
      const { temperature: _omitTemp, ...otherOptions } = options;

      form.setFieldsValue({
        temperature: temperature,
        optionsJson:
          Object.keys(otherOptions).length > 0
            ? JSON.stringify(otherOptions, null, 2)
            : "",
      });
    }
  }, [isAdvancedModalVisible, value, form]);

  const handleProfileChange = (val: string | null) => {
    onChangeProfile(val);
  };

  const normalizedProfileId = profileId?.split("/").pop() || null;

  const handleAdvancedSave = () => {
    const values = form.getFieldsValue();

    let options: Record<string, any> = {};
    if (values.optionsJson) {
      try {
        options = JSON.parse(values.optionsJson);
      } catch (e) {
        // Ignore parse error or handle it
        console.error("Invalid JSON in options", e);
      }
    }

    if (values.temperature !== undefined && values.temperature !== null) {
      options.temperature = values.temperature;
    } else {
      delete options.temperature;
    }

    // Overrides carry inference options only; model/provider/credentials come
    // from the selected AI Profile and can no longer be overridden here.
    if (Object.keys(options).length > 0) {
      onChangeOverride({ options });
    } else {
      onChangeOverride(null);
    }

    setIsAdvancedModalVisible(false);
  };

  // Options-only override: describe the option keys the override carries.
  const overrideOptions = (value?.options ?? {}) as Record<string, any>;
  const overrideOptionKeys = Object.keys(overrideOptions);
  const hasOverride = overrideOptionKeys.length > 0;

  const openRouterModels = (modelsResponse?.data || []) as any[];

  const profileOptions = profiles
    .filter((p: AiProfile) => (profileFilter ? profileFilter(p) : true))
    .map((p: AiProfile) => {
      let isFilteredOut = false;

      if (freeOnly || minContext > 0 || modalityFilter !== "all") {
        const modelId = p.aiConnection?.model || "";
        const orModel = openRouterModels.find((m: any) => m.id === modelId);

        if (freeOnly) {
          if (!modelId.endsWith(":free") && modelId !== "openrouter/free") {
            isFilteredOut = true;
          }
        }

        if (!isFilteredOut && minContext > 0) {
          const context = orModel?.context_length || 0;
          if (context < minContext) isFilteredOut = true;
        }

        if (!isFilteredOut && modalityFilter !== "all") {
          if (modalityFilter === "image" && !p.hasVision) isFilteredOut = true;
          if (modalityFilter === "text" && p.hasVision) isFilteredOut = true;
        }
      }

      return {
        label: p.name,
        value: p.id.split("/").pop(),
        disabled: isFilteredOut,
      };
    });

  const filtersContent = (
    <div className="flex flex-col gap-4 w-64 p-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarOutlined className="text-emerald-500" />
          <span className="text-sm font-medium">Free Only</span>
        </div>
        <Switch size="small" checked={freeOnly} onChange={setFreeOnly} />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Min Context Length
        </span>
        <Select
          size="small"
          value={minContext}
          onChange={setMinContext}
          options={[
            { value: 0, label: "Any" },
            { value: 8000, label: "> 8k tokens" },
            { value: 32000, label: "> 32k tokens" },
            { value: 128000, label: "> 128k tokens" },
          ]}
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Modality
        </span>
        <Select
          size="small"
          value={modalityFilter}
          onChange={setModalityFilter}
          options={[
            { value: "all", label: "All Types" },
            { value: "text", label: "Text Only" },
            { value: "image", label: "Vision/Image" },
          ]}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        {hideOverrides ? null : (
          <Button
            type="text"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => setIsAdvancedModalVisible(true)}
          >
            {hasOverride ? "Edit Overrides" : "Add Overrides"}
          </Button>
        )}
      </div>

      {help ? <p className="text-xs text-gray-500">{help}</p> : null}

      <div className="flex items-center gap-2">
        <Select
          showSearch
          optionFilterProp="label"
          allowClear
          className="flex-1"
          placeholder="Select an AI Configuration Profile..."
          loading={isLoading}
          value={normalizedProfileId}
          onChange={handleProfileChange}
          options={profileOptions}
        />
        <Popover
          content={filtersContent}
          title="Smart Filters"
          trigger="click"
          placement="bottomRight"
        >
          <Tooltip title="Filter profiles">
            <Button
              icon={<FilterOutlined />}
              className={
                freeOnly || minContext > 0 || modalityFilter !== "all"
                  ? "border-indigo-500 text-indigo-500"
                  : ""
              }
            />
          </Tooltip>
        </Popover>
      </div>

      {hasOverride && !hideOverrides ? (
        <div className="mt-2 text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200 flex items-center justify-between">
          <span>
            <strong>Inference option overrides active:</strong>{" "}
            {overrideOptionKeys.join(", ")}
          </span>
          <Button
            type="link"
            size="small"
            onClick={() => onChangeOverride(null)}
          >
            Clear Overrides
          </Button>
        </div>
      ) : null}

      <Modal
        title="Inference Option Overrides"
        open={hideOverrides ? false : isAdvancedModalVisible}
        onOk={handleAdvancedSave}
        onCancel={() => setIsAdvancedModalVisible(false)}
        width={600}
        destroyOnHidden
      >
        <p className="text-sm text-gray-600 mb-4">
          Override only inference options. The model, provider, and credentials
          always come from the selected AI Profile and cannot be overridden
          here. Only <code>temperature</code>, <code>max_tokens</code> and{" "}
          <code>top_p</code> are honoured; leave blank to inherit from the
          profile.
        </p>
        <Form form={form} layout="vertical">
          <Form.Item name="temperature" label="Temperature Override">
            <InputNumber
              min={0}
              max={2}
              step={0.1}
              placeholder="e.g. 0.7"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            name="optionsJson"
            label="Additional Options (JSON)"
            help="Only temperature, max_tokens and top_p are honoured; any other keys are ignored by the backend."
          >
            <Input.TextArea rows={4} placeholder='{"max_tokens": 1000}' />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
